/* eslint-disable @typescript-eslint/naming-convention */
import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons/faArrowLeft';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons/faArrowRight';
import { faBell } from '@fortawesome/free-solid-svg-icons/faBell';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { faHands } from '@fortawesome/free-solid-svg-icons/faHands';
import { faHandsHelping } from '@fortawesome/free-solid-svg-icons/faHandsHelping';
import { faCommentSlash } from '@fortawesome/free-solid-svg-icons/faCommentSlash';
import { faEnvelope } from '@fortawesome/free-solid-svg-icons/faEnvelope';
import { faEye } from '@fortawesome/free-solid-svg-icons/faEye';
import { faLock } from '@fortawesome/free-solid-svg-icons/faLock';
import { faLockOpen } from '@fortawesome/free-solid-svg-icons/faLockOpen';
import { faSquare } from '@fortawesome/free-solid-svg-icons/faSquare';
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes';
import { faCheckCircle } from '@fortawesome/free-regular-svg-icons/faCheckCircle';
import { faCircle } from '@fortawesome/free-regular-svg-icons/faCircle';
import { faComment } from '@fortawesome/free-regular-svg-icons/faComment';
import { BehaviorSubject, fromEvent, Observable, Subscription } from 'rxjs';
import { filter, mergeMap, skip, switchMap, take } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { HelperActivityService } from '../../../services/helper/helper-activity/helper-activity.service';
import { InterventionInstanceInterface } from '../../../models/interface/intervention-instances/intervention-instance.interface';
import { ActivityInterface } from '../../../models/interface/activity.interface';
import { InterventionInterface } from '../../../models/interface/intervention.interface';
import { HelperService } from '../../../services/helper/helper.service';
import { UserInterface } from '../../../models/interface/user.interface';
import { MessageThreadsInterface } from '../../../models/interface/message_threads.interface';
import { LessonInterface } from '../../../models/interface/lesson.interface';
import { AnswersheetInterface } from '../../../models/interface/answersheet.interface';
import { HelperFeedbackService } from '../../../services/helper/helper-feedback/helper-feedback.service';
import { InterventionInstanceConfigurationInterface } from '../../../models/interface/intervention-instances/intervention-instance.configuration.interface';
import { ProfileInterface } from '../../../models/interface/profile.interface';
import { InterventionConfigurationInterface } from '../../../models/interface/intervention-configuration.interface';
import { SkillInterface } from '../../../models/interface/skill.interface';
import { HelperSkillService } from '../../../services/helper/helper-skill/helper-skill.service';
import { HelperDialogService } from '../../../services/helper/helper-dialog/helper-dialog.service';
import { StudyActionTypes } from '../../../store/study/study.action';
import { AnswersheetsStore } from '../../../store/answersheet/component-store/answersheet.store';
import { InterventionInstanceStore } from '../../../store/intervention-instance/component-store/intervention-instance.store';
import { MessageStore } from '../../../store/message/component-store/message.store';
import { SkillStore } from '../../../store/skill/component-store/skill.store';
import { getCollaboratorsByStudyId } from '../../../store/study/study.selector';
import { UserStore } from '../../../store/user/component-store/user.store';
import { Chart, InteractionItem } from 'chart.js/auto';
import { InterventionInstanceLogInterface } from 'src/app/models/interface/intervention-instances/intervention-instance.log.interface';
import { faClock } from '@fortawesome/free-regular-svg-icons/faClock';
import { DiaryInterface } from '../../../models/interface/diary.interface';
import { DiaryStore } from '../../../store/diary/component-store/diary.store';
import { faBook } from '@fortawesome/free-solid-svg-icons/faBook';

type SendReminderBehavior = 'DEFAULT' | 'LOADING' | 'SUCCESS' | 'FAILURE';
type UnlockManuallyBehavior = 'DEFAULT' | 'LOADING' | 'SUCCESS' | 'FAILURE1'| 'FAILURE2';

type SkillDataSet = {
  label: string;
  data: number[];
  backgroundColor: string[];
};

/**
 * Component:
 * Instance details component displaying details including progress, submitted answers and activities;
 */
@Component({
  selector: 'app-instance-detail',
  templateUrl: './instance-detail.component.html',
  styleUrls: ['./instance-detail.component.scss'],
  providers: [AnswersheetsStore, DiaryStore, InterventionInstanceStore, MessageStore, SkillStore, UserStore],
})
export class InstanceDetailComponent implements OnInit, AfterViewInit, OnDestroy {
  public canvas: ElementRef;

  // Icon
  faArrowLeft = faArrowLeft;
  faArrowRight = faArrowRight;
  faLock = faLock;
  faLockOpen = faLockOpen;
  faCheck = faCheck;
  faComment = faComment;
  faCommentSlash = faCommentSlash;
  faCheckCircle = faCheckCircle;
  faCircle = faCircle;
  faHands = faHands;
  faHandsHelping = faHandsHelping;
  faEnvelope = faEnvelope;
  faTimes = faTimes;
  faBell = faBell;
  faEye = faEye;
  faSquare = faSquare;
  faClock = faClock;
  faBook = faBook;

  public intervention: InterventionInterface;
  public studyMembers: UserInterface[] = [];
  public instance: InterventionInstanceInterface;
  private questionnaireConfigurations: Map<string, InterventionInstanceConfigurationInterface>;
  public instance$: Observable<InterventionInstanceInterface>;
  public logsOfInterventionInstance$: Observable<InterventionInstanceLogInterface[]>;
  public questionnaires: LessonInterface[] = [];
  public answersheets: AnswersheetInterface[] = [];
  public answersheetsOfInterventionInstance$: Observable<AnswersheetInterface[]>;
  public activities: ActivityInterface[] = [];
  public threads: MessageThreadsInterface[] = [];
  public skills: SkillInterface[] = [];
  public hasSkills = false;

  public isLoading$: Observable<boolean>;

  // Lesson/Instance progress
  public progress = 0;
  public showChild$: Observable<string>;

  public profile: ProfileInterface;
  public profile$: Observable<ProfileInterface>;

  public activitiesSubject = new BehaviorSubject<ActivityInterface[]>([]);
  public instanceLogsSubject = new BehaviorSubject<InterventionInstanceLogInterface[]>([]);
  public pagedActivitySubject = new BehaviorSubject<ActivityInterface[]>([]);
  public pagedActivity$ = this.pagedActivitySubject.asObservable();

  public activitiesOfInstance$: Observable<ActivityInterface[]>;
  public unlockQuestionnaireAsECoachResponse$: Observable<any>;

  public sendReminderResponseText = new Map<number, BehaviorSubject<SendReminderBehavior>>();
  public unlockManuallyResponseText = new Map<number, BehaviorSubject<UnlockManuallyBehavior>>();

  public selectedIndexSubject = new BehaviorSubject<number>(0);

  public lessonSkills: Array<{ skillId: number; lessonIds: Array<number> }> = [];

  public studyCollaborator: UserInterface[] = [];

  // Translation
  public param: { lesson_one: number; lesson_two: number };

  // New Chart
  public chart: Chart<"polarArea", number[], string>;
  public dataset: { labels: string[]; datasets: SkillDataSet[] } = { labels: [], datasets: [] };

  public diaries: DiaryInterface[];
  public diariesOfStudy$: Observable<DiaryInterface[]>;

  private allThreadsOfInstance$: Observable<MessageThreadsInterface[]>;

  private skillsOfIntervention$: Observable<SkillInterface[]>;

  private collaboratorsOfStudy$: Observable<{ studyId: number; collaborators: UserInterface[] }>;

  private sendReminderEmailResponse$: Observable<any>;

  private isLoadingSubject = new BehaviorSubject<boolean>(true);
  private showChildSubject = new BehaviorSubject<string>('details');

  private subscriptions: Subscription[] = [];

  constructor(
    private helperService: HelperService,
    private helperFeedbackService: HelperFeedbackService,
    private helperActivityService: HelperActivityService,
    private helperSkillService: HelperSkillService,
    private helperDialogService: HelperDialogService,
    private answersheetsStore: AnswersheetsStore,
    private interventionInstanceStore: InterventionInstanceStore,
    private messageStore: MessageStore,
    private skillStore: SkillStore,
    private userStore: UserStore,
    private diaryStore: DiaryStore,
    private store: Store<{
      myProfile: ProfileInterface;
      skills: SkillInterface[];
      getCollaboratorsByStudyId: { studyId: number; collaborators: UserInterface[] };
    }>,
  ) {
    this.profile$ = store.select('myProfile');
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.showChild$ = this.showChildSubject.asObservable();
    this.answersheetsOfInterventionInstance$ = this.answersheetsStore.answersheetsOfInterventionInstance$;
    this.instance$ = this.interventionInstanceStore.instance$;
    this.activitiesOfInstance$ = this.interventionInstanceStore.activitiesOfInstance$;
    this.unlockQuestionnaireAsECoachResponse$ = this.interventionInstanceStore.unlockQuestionnaireAsECoachResponse$;
    this.allThreadsOfInstance$ = this.messageStore.allThreadsOfInstance$;
    this.skillsOfIntervention$ = this.skillStore.skillsOfIntervention$;
    this.sendReminderEmailResponse$ = this.userStore.sendReminderEmailResponse$;
    this.logsOfInterventionInstance$ = this.interventionInstanceStore.logsOfInterventionInstance$;
    this.diariesOfStudy$ = this.diaryStore.diariesOfStudy$;
  }

  // Helper function
  public get helper() {
    return this.helperService;
  }

  public get helperFeedback() {
    return this.helperFeedbackService;
  }

  public get helperActivity() {
    return this.helperActivityService;
  }

  public get helperSkill() {
    return this.helperSkillService;
  }

  public get helperDialog() {
    return this.helperDialogService;
  }

  @ViewChild('canvas', { static: false }) set content(content: ElementRef) {
    if (content) {
      this.canvas = content;
      this.chart = new Chart(this.canvas.nativeElement.getContext('2d'), {
        type: 'polarArea',
        data: this.dataset,
        options: {
          responsive: true,
          onHover: function(e) {
            const points = this.getElementsAtEventForMode(
              e,
              'point',
              { intersect: true },
              true,
            );
            if (points.length) {
              (e.native.target as HTMLElement).style.cursor = 'pointer';
            } else {
              (e.native.target as HTMLElement).style.cursor = 'default';
            }
          },
          scales: {
            r: {
              min: 0,
              max: 100,
              ticks: {
                backdropColor: 'rgba(255, 255, 255, 0.85)',
                z: 1,
              },
              grid: {
                z: 1,
              },
            },
          },
          plugins: {
            legend: {
              onHover: function(event) {
                (event.native.target as HTMLElement).style.cursor = 'pointer';
              },
            },
          },
        },
      });
      fromEvent(content.nativeElement, 'click').subscribe((event: Event) => {
        const activePoints: InteractionItem[] = this.chart.getElementsAtEventForMode(
          event,
          'point',
          { intersect: true },
          true,
        );
        // Array has no elements if label is clicked to disable/enable a skill from the chart
        if (activePoints.length > 0) {
          this.onSelectSkill(this.lessonSkills[activePoints[0].index].skillId);
        }
      });
    }
  }

  public onSelectSkill(skillId: number): void {
    const dialogRef = this.helperDialog.openDialogSkillByName(
      skillId,
      this.skills,
      undefined,
      this.intervention,
    );
    if (dialogRef) {
      dialogRef.afterClosed().subscribe(() => {});
    }
  }

  // Set study and its configuration from parent
  @Input()
  set _intervention(_intervention: InterventionInterface) {
    if (_intervention) {
      this.intervention = _intervention;
    }
  }

  // Set members of study from parent
  @Input()
  set _members(_members: UserInterface[]) {
    this.studyMembers = _members;
  }

  // Set collaborators of study from parent
  @Input()
  set _collaborators(_collaborators: UserInterface[]) {
    if (_collaborators) {
      this.studyCollaborator = _collaborators;
    }
  }

  @Input()
  set _instance(_instance: InterventionInstanceInterface) {
    this.instance = _instance;
  }

  @Input()
  set _questionnaires(_questionnaires: LessonInterface[]) {
    if (_questionnaires) {
      this.questionnaires = _questionnaires;
    }
  }

  @Input()
  set _initialChild(_initialChild: string) {
    this.showChildSubject = new BehaviorSubject<string>(_initialChild);
  }

  @Input()
  set _selectedIndex(_selectedIndex: number) {
    if (_selectedIndex) {
      this.selectedIndexSubject = new BehaviorSubject<number>(_selectedIndex);
    }
  }

  ngOnInit(): void {
    if (this.intervention.attributes.gamification) {
      if (this.intervention.attributes.gamification.skills_order) {
        if (this.intervention.attributes.gamification.skills_order.length > 0) {
          this.lessonSkills = [];
          this.intervention.attributes.gamification.skills_order.forEach((skillId) => {
            this.lessonSkills.push({ skillId, lessonIds: [] });
          });
        }
      }
    }

    this.interventionInstanceStore.getInstance(this.instance.id);
    this.subscriptions.push(
      this.instance$
        .pipe(
          filter((value) => !!value),
          take(1)
        )
        .subscribe((result: InterventionInstanceInterface) => {
          this.instance = result;
          this.sendReminderResponseText.clear();
          this.instance.attributes.configuration.custom_order.forEach((questionnaireId: number) => {
            this.sendReminderResponseText.set(questionnaireId, new BehaviorSubject<SendReminderBehavior>('DEFAULT'));
            this.unlockManuallyResponseText.set(questionnaireId, new BehaviorSubject<UnlockManuallyBehavior>('DEFAULT'));
          });
        }),
    );

    this.interventionInstanceStore.getInterventionInstanceLogs({ instanceId: this.instance.id });
    this.subscriptions.push(
      this.logsOfInterventionInstance$.pipe(skip(1), take(1)).subscribe((result) => {
        this.instanceLogsSubject.next(result);
      }),
    );

    this.diaryStore.getDiariesOfStudy({ studyId: this.intervention.attributes.study_id });
    this.subscriptions.push(
      this.diariesOfStudy$.pipe(skip(1), take(1)).subscribe((result) => {
        this.diaries = result;
      }),
    );

    this.skillStore.getSkillsOfIntervention({ id: this.intervention.id });
    this.subscriptions.push(
      this.skillsOfIntervention$.subscribe((result) => {
        this.skills = result;
        if (this.lessonSkills.length > 0) {
          this.hasSkills = this.questionnaires.some((lesson) => {
            return lesson.attributes.skills && lesson.attributes.skills.some((skillId) => {
              return this.intervention.attributes.gamification.skills_order.includes(skillId);
            });
          });
        }
        this.questionnaires.forEach((lesson) => {
          if (lesson.attributes.skills) {
            lesson.attributes.skills.forEach((skillId: number) => {
              const lessonSkill = this.lessonSkills.find(
                (value) => value.skillId.toString() === skillId.toString(),
              );
              if (lessonSkill !== undefined) {
                const lessonId = parseInt(lesson.id.toString(), 10);
                if (!lessonSkill.lessonIds.includes(lessonId)) {
                  lessonSkill.lessonIds.push(lessonId);
                }
              }
            });
          }
        });
        // Skills with no linked lessons are ignored
        this.lessonSkills = this.lessonSkills.filter((lessonSkill) => lessonSkill.lessonIds.length > 0);
        if (this.skills.length > 0) {
          const skillDataSetData: number[] = [];
          const skillBackgroundColors: string[] = [];
          this.lessonSkills.forEach((value) => {
            const skill = this.skills.find((skill) => skill.id.toString() === value.skillId.toString());
            if (skill !== undefined) {
              let finishedLessons = 0;
              let unlockedOrFinishedLessons = 0;
              for (const lessonId of value.lessonIds) {
                if (this.instance.attributes.progress.finished_questionnaires.includes(lessonId)) {
                  finishedLessons++;
                  unlockedOrFinishedLessons++;
                } else if (this.instance.attributes.progress.unlocked_questionnaires.includes(lessonId)) {
                  unlockedOrFinishedLessons++;
                }
              }
              this.dataset.labels.push(skill.attributes.title);
              const completionRatio = finishedLessons / unlockedOrFinishedLessons;
              skillDataSetData.push(Math.round(completionRatio * 100));
              skillBackgroundColors.push(skill.attributes.color || '#15417E');
            }
          });
          if (skillDataSetData.length > 0) {
            const skillDataSet: SkillDataSet = {
              label: 'In %',
              data: skillDataSetData,
              backgroundColor: skillBackgroundColors,
            };
            this.dataset.datasets.push(skillDataSet);
          }
        }
      }),
    );

    this.subscriptions.push(
      this.profile$
        .pipe(
          filter((user) => user !== null),
          mergeMap((value: ProfileInterface) => {
            this.profile = value;
            this.store.dispatch({
              type: StudyActionTypes.getCollaboratorsType,
              payload: { studyId: this.intervention.attributes.study_id, include: 'roles' },
            });

            this.collaboratorsOfStudy$ = this.store.select(getCollaboratorsByStudyId(this.intervention.attributes.study_id));
            return this.collaboratorsOfStudy$.pipe(
              filter((collaboratorsOfStudy) => !!collaboratorsOfStudy),
              take(1),
            );
          }),
          mergeMap(result => {
            const collaborators = result.collaborators;
            // Determine my highest role
            const myself: UserInterface = collaborators.find(user => user.id.toString() === this.profile.id.toString());
            const myRoleSlug = myself ? this.helper.getHighestRoleOfCollaborator(myself) : '';
            const isECoachManager = myRoleSlug === 'study.ecoachmanager' || myRoleSlug === 'study.owner';
            if (isECoachManager) {
              this.answersheetsStore.getAnswersheetsOfInterventionInstanceEM(this.instance.id);
            } else {
              this.answersheetsStore.getAnswersheetsOfInterventionInstance(this.instance.id);
            }
            this.answersheetsOfInterventionInstance$.subscribe((answersheets) => {
              this.answersheets = this.helperService.getAnswersheetsByNewest(answersheets);
            });
            if (isECoachManager) {
              this.interventionInstanceStore.getActivitiesOfInterventionInstanceEM({
                instanceId: this.instance.id,
                include: 'questionnaires',
              });
              return this.activitiesOfInstance$.pipe(
                skip(1),
                take(1),
                switchMap((activities) => {
                  this.activities = this.helper.getActivityByNewest(activities);
                  this.activitiesSubject.next(this.activities);
                  this.pagedActivitySubject.next(this.activitiesSubject.value.slice(0, 20));
                  this.messageStore.getAllThreadsOfInstanceEM({
                    instanceId: this.instance.id,
                  });
                  return this.allThreadsOfInstance$.pipe(skip(1), take(1));
                })
              );
            } else {
              this.interventionInstanceStore.getActivitiesOfInterventionInstance({
                instanceId: this.instance.id,
                include: 'questionnaires',
              });
              return this.activitiesOfInstance$.pipe(
                skip(1),
                take(1),
                switchMap((activities) => {
                  this.activities = this.helper.getActivityByNewest(activities);
                  this.activitiesSubject.next(this.activities);
                  this.pagedActivitySubject.next(this.activitiesSubject.value.slice(0, 20));
                  this.messageStore.getAllThreadsOfInstance({
                    instanceId: this.instance.id,
                  });
                  return this.allThreadsOfInstance$.pipe(skip(1), take(1));
                })
              );
            }
          })
        )
        .subscribe({
          next: (result) => {
            this.threads = result;
            this.isLoadingSubject.next(false);
          },
          error: () => {
            this.answersheets = [];
            this.activities = [];
            this.pagedActivitySubject.next(this.activities);
            this.threads = [];
            this.isLoadingSubject.next(false);
          },
        }),
    );
    const lessonOne =
      this.instance.attributes.progress.finished_questionnaires == null
        ? 0
        : this.instance.attributes.progress.finished_questionnaires.length;
    const lessonTwo = this.instance.attributes.configuration.custom_order.length;
    this.param = { lesson_one: lessonOne, lesson_two: lessonTwo };

    this.unlockAtDateQuestionnairesOfInstance(this.instance);
    const questionnaireConfigurations = new Map<string, InterventionInstanceConfigurationInterface>();
    for (const c in this.instance.attributes.configuration.questionnaire_configuration) {
      const config = this.instance.attributes.configuration.questionnaire_configuration[c];
      questionnaireConfigurations.set(config.id.toString(), config);
    }
    this.questionnaireConfigurations = questionnaireConfigurations;
  }

  ngAfterViewInit(): void {}

  public getConfigurationByQuestionnaireId(questionnaireId: number): InterventionInstanceConfigurationInterface {
    return this.questionnaireConfigurations.get(questionnaireId.toString());
  }

  public getQuestionnaireById(questionnaireId: number): LessonInterface {
    const qre = questionnaireId.toString();
    const found = this.questionnaires.find((config) => config.id.toString() === qre);
    return found ? found : null;
  }

  public calculateProgress(): string {
    const customOrder = this.instance.attributes.configuration.custom_order.length;
    const finished =
      this.instance.attributes.progress.finished_questionnaires == null
        ? 0
        : this.instance.attributes.progress.finished_questionnaires.length;
    if (customOrder === 0 || finished === 0) {
      this.progress = 0;
      return '0%';
    }
    this.progress = (finished / customOrder) * 100;
    return this.progress + '%';
  }

  public getLessonStatus(questionnaireId: number): string {
    const qre = questionnaireId.toString();
    const finished = this.instance.attributes.progress.finished_questionnaires;
    const foundFinished = finished?.some((finish) => qre === finish.toString());
    if (foundFinished) {
      return 'instance-detail.finished';
    }
    const unlocked = this.instance.attributes.progress.unlocked_questionnaires;
    const foundUnlocked = unlocked?.some((unlock) => qre === unlock.toString());
    if (foundUnlocked) {
      return 'instance-detail.unlocked';
    }
    const config = this.getConfigurationByQuestionnaireId(questionnaireId);
    if (config.unlock_type === 'at_date') {
      const now = Math.floor(Date.now() / 1000);
      const timeToUnlock = config.unlock_date;
      return now < timeToUnlock ? 'instance-detail.locked' : 'instance-detail.unlocked';
    }
    return 'instance-detail.locked';
  }

  public isCurrentQuestionnaireId(instance: InterventionInstanceInterface, questionnaireId: number): boolean {
    const qre = questionnaireId.toString();
    const currentQuestionnaire = instance.attributes.progress.current_questionnaire_id;
    return currentQuestionnaire != null && currentQuestionnaire.toString() === qre;
  }

  public isActiveQuestionnaireId(instance: InterventionInstanceInterface, questionnaire_id: number): boolean {
    const qre = questionnaire_id.toString();
    return instance.attributes.progress.active_questionnaires.some((id) => id.toString() === qre);
  }

  public unlockManuallyQuestionnaire(instance: InterventionInstanceInterface, questionnaireId: number) {
    const unlock_tasks = this.activities.filter(
      (activity) =>
        activity.attributes.action === 'INTERVENTION_INSTANCE_CAN_UNLOCK' &&
        (activity.attributes.finished.toString() === '0' ||
          activity.attributes.finished.toString() === '' ||
          activity.attributes.finished.toString() === 'pending')
    );
    let activityId: number;
    if (unlock_tasks.length > 0) {
      activityId = unlock_tasks[0].id;
    }
    const unlockManuallyResponseEntry = this.unlockManuallyResponseText.get(questionnaireId);
    if (unlockManuallyResponseEntry !== undefined) {
      unlockManuallyResponseEntry.next('LOADING');
      this.interventionInstanceStore.unlockQuestionnaireAsECoach({
        instance,
        unlockedQuestionnaires: [questionnaireId],
        activityId,
      });
      this.subscriptions.push(
        this.unlockQuestionnaireAsECoachResponse$
          .pipe(
            filter((value) => !!value),
            take(1),
            mergeMap((result) => {
              if (result instanceof HttpErrorResponse) {
                const error: HttpErrorResponse = result['response']['content'];
                if (error.error.errors[0].code === '89' && error.error.errors[0].status === '403') {
                  unlockManuallyResponseEntry.next('FAILURE1');
                } else {
                  unlockManuallyResponseEntry.next('FAILURE2');
                }
              }
              this.interventionInstanceStore.getInstance(this.instance.id);
              return this.instance$.pipe(skip(1), take(1));
            })
          )
          .subscribe({
            next: (result: InterventionInstanceInterface) => {
              unlockManuallyResponseEntry.next('SUCCESS');
              this.instance = result;
              setTimeout(() => {
                unlockManuallyResponseEntry.next('DEFAULT');
              }, 10000);
            },
            error: () => {
              unlockManuallyResponseEntry.next('FAILURE2');
              setTimeout(() => {
                unlockManuallyResponseEntry.next('DEFAULT');
              }, 10000);
            },
          }),
      );
    }
  }

  // Display and navigate to child component relative to this path
  public showChild(child: string): void {
    this.showChildSubject.next(child);
  }

  public sendReminder(userId: number, questionnaireId: number): void {
    const sendReminderResponseEntry = this.sendReminderResponseText.get(questionnaireId);
    if (sendReminderResponseEntry !== undefined) {
      sendReminderResponseEntry.next('LOADING');
      this.userStore.sendReminderEmail({ userId,
        questionnaireId,
        interventionInstanceId: this.instance.id,
      });
      this.subscriptions.push(
        this.sendReminderEmailResponse$.pipe(skip(1), take(1)).subscribe({
          next: (result) => {
            if (result instanceof HttpResponse) {
              sendReminderResponseEntry.next('SUCCESS');
            } else {
              sendReminderResponseEntry.next('FAILURE');
            }
            setTimeout(() => {
              sendReminderResponseEntry.next('DEFAULT');
            }, 10000);
          },
          error: () => {
            sendReminderResponseEntry.next('FAILURE');
            setTimeout(() => {
              sendReminderResponseEntry.next('DEFAULT');
            }, 10000);
          },
        }),
      );
    }
  }

  public updatePagedActivities(event: ActivityInterface[]) {
    if (event) {
      this.pagedActivitySubject.next(event);
    }
  }

  unlockAtDateQuestionnairesOfInstance(instance: InterventionInstanceInterface) {
    const finished = this.instance.attributes.progress.finished_questionnaires ?? [];
    const unlocked = this.instance.attributes.progress.unlocked_questionnaires ?? [];
    const finishedAndUnlocked = finished.concat(unlocked);
    const instanceConfiguration = instance.attributes.configuration.questionnaire_configuration;
    const questionnairesToUnlock: number[] = [];
    instanceConfiguration.forEach((config) => {
      if (!finishedAndUnlocked.includes(config.id) && config.unlock_type === 'at_date') {
        const now = Math.floor(Date.now() / 1000);
        const timeToUnlock = config.unlock_date;
        if (now > timeToUnlock) {
          questionnairesToUnlock.push(config.id);
        }
      }
    });
    if (questionnairesToUnlock.length > 0) {
      this.interventionInstanceStore.unlockQuestionnaireAsECoach({
        instance,
        unlockedQuestionnaires: questionnairesToUnlock,
      });
      this.unlockQuestionnaireAsECoachResponse$.pipe(
        filter((value) => !!value),
        take(1),
      );
    }
  }

  public isCanceled(instance: InterventionInstanceInterface): boolean {
    return instance.attributes.progress.current_state === 'canceled';
  }

  public isPaused(instance: InterventionInstanceInterface): boolean {
    return instance.attributes.progress.current_state === 'paused';
  }

  public openDialogInterventionLesson(questionnaireId: number, studyId: number): void {
    this.helperDialog
      .openDialogInterventionLesson(questionnaireId, studyId)
      .afterClosed()
      .subscribe(() => {});
  }

  public getCondition(questionnaireId: number): string {
    const foundConfig = this.getDefaultConfigurationOfQuestionnaire(questionnaireId);
    if (foundConfig !== null && foundConfig.condition) {
      if (Array.isArray(foundConfig.condition)) {
        if (foundConfig.condition.length > 0) {
          let condition = foundConfig.condition[0].description;
          for (let i = 1 ; i < foundConfig.condition.length; i++) {
            const cfg = foundConfig.condition[i];
            condition = condition + ' & ' + cfg.description;
          }
          return condition;
        }
        return null;
      }
      return foundConfig.condition.description || JSON.stringify(foundConfig.condition);
    } else {
      return null;
    }
  }

  // Returns default configuration of a questionnaire
  public getDefaultConfigurationOfQuestionnaire(questionnaireId: number): InterventionConfigurationInterface {
    const found = this.intervention.attributes.default_configuration.questionnaire_configuration.find(
      (questionnaire) => questionnaire.id.toString() === questionnaireId.toString()
    );
    return found ? found : null;
  }

  public getQuestionnaireUnlockedSince(questionnaireId: number) {
    return this.instanceLogsSubject.value.find(
      (log) => log.attributes.questionnaire_id.toString() === questionnaireId.toString()
    )?.attributes.created_at;
  }

  public isActiveOrAwaiting(instance: InterventionInstanceInterface) {
    const state = instance.attributes.progress.current_state;
    return state === 'active' || state === 'awaiting_next_questionnaire';
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
